
import React from 'react';
import { ServiceCardProps } from '../types';

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, features, color }) => {
  const colorMap = {
    blue: 'border-blue-100/50 hover:border-blue-400 bg-blue-50/40 shadow-blue-500/5 hover:shadow-blue-500/20',
    purple: 'border-purple-100/50 hover:border-purple-400 bg-purple-50/40 shadow-purple-500/5 hover:shadow-purple-500/20',
    green: 'border-emerald-100/50 hover:border-emerald-400 bg-emerald-50/40 shadow-emerald-500/5 hover:shadow-emerald-500/20',
  };

  const iconBg = {
    blue: 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white',
    purple: 'bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white',
    green: 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white',
  };

  const checkColor = {
    blue: 'text-blue-500',
    purple: 'text-purple-500',
    green: 'text-emerald-500',
  };

  const highlightText = {
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    green: 'text-emerald-700',
  };

  return (
    <div className={`p-8 rounded-[2.5rem] backdrop-blur-xl border-2 transition-all duration-500 flex flex-col h-full ${colorMap[color]} group hover:-translate-y-3`}>
      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 ${iconBg[color]} text-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl shadow-current/20`}>
        <i className={icon}></i>
      </div>
      <h3 className={`text-2xl font-black mb-4 transition-colors duration-300 ${highlightText[color]}`}>{title}</h3>
      <p className="text-slate-600 mb-8 flex-grow leading-relaxed font-semibold">
        {description}
      </p>
      <ul className="space-y-4">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-3 text-sm font-bold text-slate-700">
            <span className={`flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm ${checkColor[color]}`}>
              <i className="fas fa-check text-[10px]"></i>
            </span>
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Services: React.FC = () => {
  return (
    <section id="services" className="py-24 relative bg-gradient-to-tr from-emerald-50/50 via-white to-blue-50/30 scroll-mt-24">
      <div className="container mx-auto px-6 relative z-10">
        <div className="mb-16 text-center max-w-2xl mx-auto">
          <div className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-black uppercase tracking-widest mb-6">
            Expert Solutions
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-[1.1]">Built for Your <span className="gradient-text">Business.</span></h2>
          <p className="text-slate-600 text-lg font-semibold leading-relaxed">
            We design and build custom web applications tailored to your workflows—with scalable cloud infrastructure, reliable hosting, and ongoing support.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          <ServiceCard
            color="blue"
            icon="fas fa-code"
            title="Custom Web Applications & Hosting"
            description="We design and build secure, high-performance web applications aligned to your business workflows, with reliable hosting and continuous support."
            features={[
              "Custom UI/UX Design & Prototyping",
              "Mobile-First, Responsive Applications",
              "Scalable Cloud & Serverless Architecture",
              "API-Driven Integrations"
            ]}
          />
        </div>
      </div>
    </section>
  );
};

export default Services;
